import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import type { IDst, IWeekDay } from '@/types/calendar';

import { convertWeekdayShortNameIntoIndex, formatDate } from '@/helpers';
import { FULL_WEEKDAY_MONTH_DAY_FORMAT } from '@/config/variables';
import { colors, otherColors } from '@/styles/colors';
import { CrossIcon } from '@/assets/icons';
import { Button } from '@/components/common';
import { useWeek } from '@/hooks';
import { CalendarDisplayRange } from '@/store/enums';

interface IProps {
  dst: IDst;
  handleClose: () => void;
  day: IWeekDay;
}

const DlsModalContent = ({ dst, day, handleClose }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { selectDays, selectDayOffset } = useWeek();

  return (
    <Container onClick={event => event.stopPropagation()}>
      <div className="d-flex justify-content-between align-items-center">
        <UpperNote>{t`dls.warning`}</UpperNote>
        <CloseIcon onClick={handleClose} />
      </div>
      <Title>{t`dls.blockTitle`}</Title>
      <Message>
        {t(`dls.${dst.tzOffsetDiff < 0 ? 'end' : 'start'}.infoWeek`, {
          date: formatDate(dst.date, FULL_WEEKDAY_MONTH_DAY_FORMAT),
        })}
      </Message>
      <StyledButton
        onClick={() => {
          selectDays(CalendarDisplayRange.ONE_DAY);
          selectDayOffset(convertWeekdayShortNameIntoIndex(day.dayName));
        }}
      >
        {t`dls.dayViewButton`}
      </StyledButton>
    </Container>
  );
};

export { DlsModalContent };

const Container = styled.div`
  font-family: barlow;
  font-size: 13px;
  line-height: 16px;
  max-width: 100%;
`;

const UpperNote = styled.div`
  color: ${colors.yellow.c600};
  font-size: 10px;
  font-weight: 700;
  line-height: 12px;
`;

const Title = styled.div`
  font-weight: 700;
  color: ${otherColors.outer_space};
`;

const Message = styled.div`
  color: ${otherColors.calendar.dlsTime};
  font-weight: 400;
  margin: 1rem 0;
  max-width: 100%;
  white-space: break-spaces;
`;

const CloseIcon = styled.div`
  width: 12px;
  height: 12px;

  cursor: pointer;

  background-image: url(${CrossIcon});
  background-size: 12px;
  background-repeat: no-repeat;
  background-position: center;
`;

const StyledButton = styled(Button)`
  border-radius: 0.5rem;
  width: 100%;
  &&& {
    height: 40px;
    font-weight: 600;
    line-height: 19px;
  }
`;
